import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import AiScreeningNotice from "../../../components/AiScreeningNotice";
import InsurancePoliciesTable from "../../../components/InsurancePoliciesTable";
import CourseCertificateUploadErrors from "../../../components/CourseCertificateUploadErrors";
import DropZoneFeedback from "../../../components/DropZoneFeedback";
import RoundedCard from "../../../components/RoundedCard";
import { ANALYZING_REFETCH_INTERVAL_MS } from "../../../hooks/analyzingRefetch";
import { useInsurancePolicies } from "@/hooks/useInsurancePolicies";
import { isPdfFile, useFileDropZone } from "../../../hooks/useFileDropZone";
import { useInvalidateReportingStatus } from "../../../hooks/useInvalidateReportingStatus";
import { useUploadInsurancePoliciesBatch } from "@/hooks/useInsurancePolicies";
import { isPolicyAnalyzingStatus } from "../../../models/policyStatus";
import InsurancePolicyUploadDialog from "./InsurancePolicyUploadDialog";

type InsurancePoliciesDocumentTabProps = {
  detailPathPrefix?: string;
  description: string | null;
  showStatusColumn?: boolean;
  title: string;
};

const INSURANCE_POLICIES_PAGE_SIZE = 10;
const MAX_ANALYZING_REFETCH_ATTEMPTS = 10;
const INVALID_DROP_MESSAGE =
  "Slip kun PDF-filer for at uploade forsikringspolicer.";

const InsurancePoliciesDocumentTab = ({
  detailPathPrefix = "",
  description,
  showStatusColumn = true,
  title,
}: InsurancePoliciesDocumentTabProps) => {
  const [page, setPage] = useState(1);
  const analyzingRefetchAttemptsRef = useRef(0);
  const hadAnalyzingPoliciesRef = useRef(false);
  const navigate = useNavigate();
  const invalidateReportingStatus = useInvalidateReportingStatus();
  const uploadInsurancePoliciesMutation = useUploadInsurancePoliciesBatch();
  const uploadErrors = uploadInsurancePoliciesMutation.data?.errors ?? [];
  const {
    data: response,
    isError,
    isFetching,
    isLoading,
    refetch,
  } = useInsurancePolicies({
    page,
    page_size: INSURANCE_POLICIES_PAGE_SIZE,
  });

  const hasAnalyzingPolicies =
    response?.items.some((policy) => isPolicyAnalyzingStatus(policy.status)) ??
    false;

  useEffect(() => {
    if (!hasAnalyzingPolicies) {
      if (hadAnalyzingPoliciesRef.current) {
        invalidateReportingStatus();
      }

      hadAnalyzingPoliciesRef.current = false;
      analyzingRefetchAttemptsRef.current = 0;

      return undefined;
    }

    hadAnalyzingPoliciesRef.current = true;

    if (analyzingRefetchAttemptsRef.current >= MAX_ANALYZING_REFETCH_ATTEMPTS) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      if (
        analyzingRefetchAttemptsRef.current >= MAX_ANALYZING_REFETCH_ATTEMPTS
      ) {
        window.clearInterval(intervalId);

        return;
      }

      analyzingRefetchAttemptsRef.current += 1;
      void refetch();
    }, ANALYZING_REFETCH_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [hasAnalyzingPolicies, invalidateReportingStatus, refetch]);

  const handlePolicyClick = (policyId: string) => {
    void navigate(`${detailPathPrefix}forsikring/${policyId}`);
  };

  const handlePolicyDrop = useCallback(
    (files: File[]) => {
      uploadInsurancePoliciesMutation.reset();
      uploadInsurancePoliciesMutation.mutate(files);
    },
    [uploadInsurancePoliciesMutation],
  );

  const { dropError, dropZoneProps, isDragActive } = useFileDropZone({
    acceptFile: isPdfFile,
    disabled: uploadInsurancePoliciesMutation.isPending,
    invalidDropMessage: INVALID_DROP_MESSAGE,
    onFilesDrop: handlePolicyDrop,
  });

  return (
    <RoundedCard
      className={[
        "cle-rounded-card--drop-target",
        isDragActive ? "cle-rounded-card--drag-active" : undefined,
      ]
        .filter(Boolean)
        .join(" ")}
      bodyClassName="p-0 mt-3"
      header={title}
      description={
        // Som på kursusbeviser: policer kan trækkes direkte ind på kortet, så
        // oplysningen må stå her og ikke kun i upload-dialogen.
        <>
          {description ? <>{description} </> : null}
          <AiScreeningNotice subject="Forsikringspolicer" variant="short" />
        </>
      }
      action={<InsurancePolicyUploadDialog />}
      aria-busy={uploadInsurancePoliciesMutation.isPending}
      {...dropZoneProps}
    >
      <DropZoneFeedback
        actionError={
          uploadInsurancePoliciesMutation.isError ? (
            <span className="fw-semibold">
              Forsikringspolicerne kunne ikke uploades lige nu. Prøv igen.
            </span>
          ) : uploadErrors.length > 0 ? (
            <CourseCertificateUploadErrors errors={uploadErrors} />
          ) : null
        }
        actionStatus={
          uploadInsurancePoliciesMutation.isPending ? (
            <span className="fw-semibold">Uploader forsikringspolicer...</span>
          ) : null
        }
        dropError={dropError}
        dropMessage="Slip PDF-filer her for at uploade"
        isDragActive={isDragActive}
      />
      <InsurancePoliciesTable
        policies={response?.items ?? []}
        isError={isError}
        isLoading={isLoading}
        onPolicyClick={handlePolicyClick}
        showStatusColumn={showStatusColumn}
        pagination={
          response
            ? {
                isDisabled: isFetching,
                onPageChange: setPage,
                page: response.page,
                total: response.total,
                totalPages: response.totalPages,
              }
            : undefined
        }
      />
    </RoundedCard>
  );
};

export default InsurancePoliciesDocumentTab;
